import { useEffect, useState } from "react";

import * as yup from "yup";
import Link from "next/link";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler, useForm } from "react-hook-form";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "reactstrap";
import Image from "next/image";
import { useRouter } from "next/router";

import classes from "@/styles/auth/login.module.css";
import { routes } from "@/utils/routes";
import InputGroup from "../common/input-group";
import useAlertBar from "@/hooks/useAlertBar";
import {
  loginV2,
  isMfaChallenge,
  mapAuthError,
} from "@/services/authServiceV2";
import Hero from "../generic/hero";
import About from "../generic/about";
import { useIsMobile } from "@/hooks/useIsMobile";
import { shipvistaLargeLogo } from "@/utils/images";
import BlogCarousel from "../generic/blog-carousel";
import { postLoginHandoff } from "./postLoginHandoff";

type Inputs = {
  email: string;
  password: string;
};

const validationSchema = yup.object({
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email Address is required"),
  password: yup.string().required("Password is required"),
});

/**
 * The reason the SPA bounced the user to login, carried as the `e` query param.
 * This mirrors the SPA's `LoginReason` union (`session | password-changed`,
 * ShipvistaAppV2 `src/auth/redirect.ts`) — that type lives in a SEPARATE repo, so
 * it is NOT imported here; `e` is an opaque string at this seam. Unknown values
 * fall through to the session-expired copy (default arm) so a future reason
 * degrades to the generic message, never a blank banner.
 */
function loginBanner(e: string | string[] | undefined): {
  copy: string;
  tone: "info" | "warning";
} | null {
  switch (typeof e === "string" ? e : "") {
    case "password-changed":
      // The user just changed their password; v2 revoked the session family by
      // design — expected, not an expiry. Calm confirmation tone.
      return {
        copy: "Password changed — please sign in again with your new password.",
        tone: "info",
      };
    case "session":
      return {
        copy: "Your session has expired. Please sign in again.",
        tone: "warning",
      };
    default:
      // Unknown / absent `e`: no banner. (The fallback arm for a future reason
      // is the session-expired copy ONLY when an `e` is present but unrecognized;
      // a plain /login with no param renders no banner.)
      if (e === undefined || e === "") return null;
      return {
        copy: "Your session has expired. Please sign in again.",
        tone: "warning",
      };
  }
}

export default function Login() {
  const { openAlertBar } = useAlertBar();
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  // `router.query.e` is undefined on first static render, then hydrates from the
  // URL — `loginBanner` returns null for undefined so no banner flashes, then the
  // correct banner renders once the query is available client-side.
  const banner = loginBanner(router.query.e);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>({
    resolver: yupResolver(validationSchema),
    criteriaMode: "all",
    shouldFocusError: true,
  });

  const onSubmit: SubmitHandler<Inputs> = async (formData) => {
    setLoading(true);
    try {
      const email = formData.email
        ? formData.email.toLowerCase()
        : formData.email;
      const { data } = await loginV2({ email, password: formData.password });

      // Arm 1 — MFA challenge: no session tokens yet. Stash ONLY the 5-min challenge
      // token (never a session token) and route to the challenge screen.
      if (isMfaChallenge(data)) {
        sessionStorage.setItem(
          "sv_mfa_challenge",
          JSON.stringify({ mfaToken: data.mfaToken, userId: email })
        );
        router.push(routes.mfaChallenge);
        return;
      }

      // Arm 2 — tokens issued: hand off to the SPA (single seam). `expiresAt` is the
      // 15-min access-token expiry; v2 does not expose a separate refresh expiry on login.
      postLoginHandoff({
        accessTokenString: data.accessToken,
        accessExpireAt: data.expiresAt,
        refreshTokenString: data.refreshToken,
        refreshExpireAt: "",
      });
    } catch (error) {
      setLoading(false);
      const { message } = mapAuthError(
        error,
        "Unexpected error occurred while trying to log you in. Please try again."
      );
      openAlertBar(message, false);
    }
  };

  const redirectToHome = () => {
    router.push(routes.home);
  };

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  return (
    <>
      <Container className="my-5">
        <Card className={`border-0 ${classes.mainCard} shadow`}>
          <Row>
            {useIsMobile() ? null : (
              <Col
                className={
                  classes.col1 + " d-flex align-items-center flex-wrap rounded"
                }
                xs={12}
                lg={7}
              >
                <div className="mx-auto">
                  <div className="text-center ">
                    <Image
                      src={shipvistaLargeLogo}
                      alt="Shipvista Logo"
                      className="img-fluid"
                      width={320}
                      height={100}
                    />
                  </div>
                  <div className="mb-5">
                    <BlogCarousel />
                  </div>
                </div>
              </Col>
            )}
            <Col xs={12} lg={5}>
              <div className="mx-auto p-3">
                <Form
                  onSubmit={handleSubmit(onSubmit)}
                  className="my-4 needs-validation"
                >
                  <h2 className="text-center mb-4">Login</h2>
                  {banner && (
                    <Alert
                      color={banner.tone}
                      className="mb-4"
                      fade={false}
                      data-testid="login-banner"
                    >
                      {banner.copy}
                    </Alert>
                  )}
                  <InputGroup
                    required
                    label="Email Address"
                    name="email"
                    register={register}
                    error={errors.email?.message}
                  />
                  <InputGroup
                    required
                    label="Password"
                    type="password"
                    name="password"
                    register={register}
                    error={errors.password?.message}
                  />
                  <Row>
                    <Col sm={12}>
                      <Link href={routes.forgotPassword}>Forgot Password?</Link>
                    </Col>
                    <Col className="text-end">
                      <div className="">
                        Don&#39;t have an account{" "}
                        <Link href={routes.register}>Sign Up Now?</Link>
                      </div>
                    </Col>
                  </Row>

                  <Button
                    type="submit"
                    disabled={loading}
                    className={`w-100 mt-2 ${classes.submitBtn}`}
                  >
                    LOGIN
                    {loading && <Spinner size="sm" className="ms-3" />}
                  </Button>
                </Form>
              </div>
            </Col>
          </Row>
        </Card>
      </Container>
      <div className={classes.overLay} onClick={redirectToHome}></div>
      <div className={classes.container} onClick={redirectToHome}>
        <About />
      </div>
    </>
  );
}
